import React, { useMemo } from 'react';
import format from 'date-fns/format';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-apollo';

import Spinner from '../../../../components/global/Spinner';
import Alert from '../../../../components/global/Alert';
import { getAsset } from '../../../../util/assets';
import { AlbumFolderBulkShippingOrderSummary } from '../../../../graphql/queries/album-folder-bulk-shipping';
import { buildRows } from './helpers';

const ITEM_IMAGE_PLACEHOLDER = getAsset('/icon-package-3d.svg');

const BulkShippingOrderSummaryPrint = () => {
  const { albumFolderId, albumFolderBulkShippingId } = useParams();

  const { data: { albumFolder } = {}, loading } = useQuery(
    AlbumFolderBulkShippingOrderSummary,
    {
      variables: {
        id: albumFolderId,
        album_folder_bulk_shipping_id: albumFolderBulkShippingId,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const bulkShipping = albumFolder?.bulk_shippings?.records?.[0];
  const prints = bulkShipping?.purchased_custom_prints?.records ?? [];
  const createdAt = bulkShipping?.created_at
    ? new Date(bulkShipping.created_at)
    : null;

  const rows = useMemo(() => buildRows(prints), [prints]);

  const stats = useMemo(() => {
    const groups = new Set(
      prints.map(print => print?.album?.id).filter(Boolean),
    );

    return {
      groups: groups.size,
      orders: bulkShipping?.transactions?.total ?? 0,
      items: bulkShipping?.purchased_custom_prints?.total ?? prints.length,
      organization: albumFolder?.account?.name ?? '—',
    };
  }, [prints, bulkShipping, albumFolder]);

  if (loading && !albumFolder) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  if (!bulkShipping) {
    return (
      <Alert type="gray-light" className="text-light text-center" borderless>
        This bulk shipment could not be found.
      </Alert>
    );
  }

  return (
    <div
      id={`album-folder-${albumFolder?.id}`}
      className="bulk-shipping-order-summary-print p-3"
    >
      <h4 className="mb-2">
        Order summary: {albumFolder?.name}
        {createdAt ? ` — ${format(createdAt, 'MMMM dd, yyyy')}` : ''}
      </h4>

      <div className="bulk-shipping-order-summary-stats d-flex flex-wrap mb-2 text-sm">
        <span className="mr-3">
          <span className="text-bold">Groups:</span> {stats.groups}
        </span>
        <span className="mr-3">
          <span className="text-bold">Orders:</span> {stats.orders}
        </span>
        <span className="mr-3">
          <span className="text-bold">Ordered items:</span> {stats.items}
        </span>
        <span className="mr-3">
          <span className="text-bold">Organization:</span> {stats.organization}
        </span>
      </div>

      {rows.length === 0 && (
        <Alert type="gray-light" className="text-light text-center" borderless>
          This bulk shipment has no orders.
        </Alert>
      )}

      {rows.length > 0 && (
        <div className="bulk-shipping-order-summary-table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Album Name</th>
                <th>Preview</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Phone</th>
                <th className="bulk-shipping-order-summary-items">Items</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.key}>
                  <td>{row.group}</td>
                  <td>
                    <img
                      src={row.imageUrl ?? ITEM_IMAGE_PLACEHOLDER}
                      alt=""
                      className="bulk-shipping-order-summary-thumb"
                      width="56"
                      height="56"
                    />
                  </td>
                  <td>{row.customerName}</td>
                  <td>{row.customerEmail}</td>
                  <td>{row.customerPhone}</td>
                  <td className="bulk-shipping-order-summary-items">
                    {row.itemGroups.map(group =>
                      group.isPackage ? (
                        <div
                          key={group.key}
                          className="bulk-shipping-order-summary-package"
                        >
                          <p className="text-bold mb-0">
                            {group.packageName || 'Package'}
                          </p>
                          <ul className="mb-0 pl-3">
                            {group.lines.map(line => (
                              <li key={line.key}>
                                {line.quantity} - {line.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <ul key={group.key} className="mb-0 pl-3">
                          {group.lines.map(line => (
                            <li key={line.key}>
                              {line.quantity} - {line.name}
                            </li>
                          ))}
                        </ul>
                      ),
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BulkShippingOrderSummaryPrint;
